import { Badge, Card, Grid, Group, Text } from "@mantine/core"
import { createColumnHelper } from "@tanstack/react-table"
import React from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { CreateOperationsAdminVenueBookingAction } from "src/components/operations/administrator/bookings/OperationsAdminVenueBookingActions"
import { DataTable } from "src/components/ui/DataTable"
import { LoadMoreButton } from "src/components/ui/LoadMoreButton"
import { EmptyPageComponent } from "src/components/ui/Misc"
import { ModuleErrors } from "src/components/ui/ModuleErrors"
import { PageFilters, usePageFilters } from "src/components/ui/PageFilters"
import { PageHeader } from "src/components/ui/PageHeader"
import { PageLayout } from "src/components/ui/PageLayout"
import { BookingStatusTag } from "src/screens/agency/operations/administrator/bookings/AgencyOperationsAdminBookings"
import { useActions, useAppState } from "src/state"
import { SetMetaInput } from "src/state/Module"
import { BookingFragment, Booking_Status_Enum, Bookings_Bool_Exp } from "src/state/gql/_types"
import { formatDate, formatTime } from "src/utils/dates"

export function OperationsAdminVenueBookings() {

    const navigate = useNavigate()
    const location = useLocation();
    const { itemsList, errors, meta, loadingMore, loading, meta: { limit, offset, sortKey, sortOrder } } = useAppState(state => state.bookings)
    const {
        subscribeToItems,
        unsubscribeFromItems,
        getMoreItems,
        resetModule,
        setMeta,
    } = useActions().bookings
    const { view } = useAppState(state => state.ui)

    const {
        filters,
        filterParams
    } = usePageFilters<Bookings_Bool_Exp['_and'], BookingFragment>({
        baseFilters: [
            { status: { _neq: Booking_Status_Enum.Deleted } }
        ],
        clickFilters: [],
        searchFilterFields: [],
        searchFilterRelations: {},
        clickFilterField: 'status',
        clickFilterValues: {
            accepted: { label: 'Accepted', filter: [{ status: { _eq: Booking_Status_Enum.Accepted } }] },
            declined: { label: 'Declined', filter: [{ status: { _eq: Booking_Status_Enum.Declined } }] },
            draf: { label: 'Draft', filter: [{ status: { _eq: Booking_Status_Enum.Draft } }] },
            offered: { label: 'Offered', filter: [{ status: { _eq: Booking_Status_Enum.Offered } }] },
            completed: { label: 'Completed', filter: [{ status: { _eq: Booking_Status_Enum.Completed } }] },
            cancelled: { label: 'Cancelled', filter: [{ status: { _eq: Booking_Status_Enum.Cancelled } }] },
        }
    })

    React.useEffect(() => {
        subscribeToItems(filters);
        return () => {
            unsubscribeFromItems()
        }
    }, [filters, limit, offset, sortKey, sortOrder, subscribeToItems, unsubscribeFromItems, resetModule])

    const columnHelper = createColumnHelper<BookingFragment>()

    const columns = React.useMemo(() => [
        columnHelper.accessor('id', {
            header: 'Id'
        }),
        columnHelper.accessor(row => row.booking_status, {
            id: 'status',
            meta: {
                relationKey: 'thread_status',
                sortKey: 'value'
            },
            header: 'Status',
            cell: row => <BookingStatusTag status={row.getValue()!} />
        }),
        columnHelper.accessor(row => row.venue?.name, {
            id: 'venue_name',
            meta: {
                relationKey: 'venue',
                sortKey: 'name'
            },
            header: 'Venue'
        }),
        columnHelper.accessor(row => row.area?.name, {
            id: 'area_name',
            meta: {
                relationKey: 'area',
                sortKey: 'name'
            },
            header: 'Area'
        }),
        columnHelper.accessor(row => row.talent?.dj_display, {
            id: 'talent_name',
            meta: {
                relationKey: 'talent',
                sortKey: 'first_name'
            },
            header: 'Talent'
        }),
        columnHelper.accessor('date', {
            header: 'Date',
            cell: row => formatDate(row.getValue(), 'MM/DD/YY')
        }),
        columnHelper.accessor('set_start_time', {
            header: 'Set Start Time',
            cell: row => formatTime(row.getValue())
        }),
        columnHelper.accessor('set_end_time', {
            header: 'Set End Time',
            cell: row => formatTime(row.getValue())
        }),
        columnHelper.accessor('poc', {
            header: 'POC'
        }),
        columnHelper.accessor('created_at', {
            header: 'Created At',
            cell: row => formatDate(row.getValue(), 'MM/DD/YY')
        }),
        columnHelper.accessor('updated_at', {
            header: 'Updated At',
            cell: row => formatDate(row.getValue(), 'MM/DD/YY')
        }),
    ], [columnHelper])

    const initialState = {
        columnVisibility: {
            id: false
        }
    }

    const handleMetaChange = (meta: SetMetaInput<BookingFragment>) => {
        setMeta(meta)
    }

    const handleSelect = (id: string) => {
        navigate(`/bookings/${id}`, { state: { drawerFrom: location } })
    }

    const renderCards = () => {
        return (
            <React.Fragment>
                <Grid>
                    {itemsList.map((i, k) => {
                        return (
                            <Grid.Col span={6} key={k}>
                                <Card
                                    component={Link}
                                    to={`/bookings/${i.id}`}
                                    shadow="xs"
                                    className="card-theme-global"
                                >
                                    <Group justify='space-between'>
                                        <Text fw={500}>#{i.id}</Text>
                                        <Badge variant="outline">{i.status}</Badge>
                                    </Group>
                                    <Text size="sm">{i.id}</Text>
                                </Card>
                            </Grid.Col>
                        )
                    })}
                </Grid>
                <LoadMoreButton meta={meta} onLoadMore={getMoreItems} loading={loadingMore} />
            </React.Fragment>
        )
    }

    const renderList = () => {
        return (
            <DataTable<BookingFragment>
                columns={columns}
                data={itemsList}
                loading={loading}
                meta={meta}
                onMetaChange={handleMetaChange}
                initialState={initialState}
                onSelectItem={handleSelect}
            />
        )
    }

    return (
        <PageLayout
            header={
                <PageHeader
                    title={`Bookings`}
                    renderAction={
                        <Group>
                            <CreateOperationsAdminVenueBookingAction />
                        </Group>
                    }
                />
            }
        >
            <PageFilters options={filterParams} />
            <ModuleErrors errors={errors} />
            {itemsList.length ? (<> {view === 'list' ? renderList() : renderCards()} </>) : (<EmptyPageComponent label='Bookings' />)}
        </PageLayout>
    )

}